/**
 * Navigator Tuning · Plan detail provider rows (feature 130): one card per provider of a
 * specialty, from a replay view or (read-only plans) the recorded result rows, and the
 * Best Match breakdown in pts within the shown config.
 */
import { pts } from './parameters';
import type {
  BreakdownItem,
  CutReason,
  PlanSearch,
  ReplayCandidate,
  ScoreEra,
} from '../types/navigator-tuning.type';

const FACTOR_LABEL: Record<string, string> = {
  vector_similarity: 'Fit',
  years_experience: 'Experience',
  years_in_business: 'In business',
  certifications_array: 'Certs',
  professional_licenses: 'Licenses',
  professional_associations: 'Associations',
  awards_recognitions: 'Awards',
  is_bquest_preferred: 'bQuest preferred',
  target_age_range: 'Target age',
};

const CUT_LABEL: Record<CutReason, string> = {
  min_vector_score: 'Below minimum fit',
  top_k: 'Cut by providers per specialty',
  sp_ineligible: 'Not eligible for this specialty',
};

const scoreLabel = (score: number | null, era: ScoreEra | null) =>
  score === null ? '—' : era === 'C' ? `${pts(score)} pts` : `${score.toFixed(2)} (legacy scale)`;

/** One card row, from a replay view or (pre-tuning) the recorded result rows. */
export type Row = {
  providerId: string;
  name: string;
  rank: number | null;
  score: number | null;
  relevance: number | null;
  quality: number | null;
  breakdown: BreakdownItem[] | null;
  cutReason: CutReason | null;
  baselineRank?: number | null;
};

export const fromReplay = (c: ReplayCandidate): Row => ({
  providerId: c.providerId,
  name: c.name ?? c.providerId,
  rank: c.finalRank,
  score: c.score,
  relevance: c.relevanceScore,
  quality: c.qualityScore,
  breakdown: c.breakdown,
  cutReason: c.cutReason,
  baselineRank: c.baselineRank,
});

export const fromRecorded = (search: PlanSearch): Row[] =>
  search.results.map((r) => ({
    providerId: r.providerId,
    name: r.name,
    rank: r.rank,
    score: r.score,
    relevance: r.relevanceScore,
    quality: r.qualityScore,
    breakdown: r.breakdown,
    cutReason: null,
  }));

/** ▲▼/new/out of Tuned against the Baseline; empty when unchanged. */
function delta(row: Row): { label: string; good: boolean } | null {
  if (row.baselineRank === undefined || row.baselineRank === row.rank) return null;
  if (row.baselineRank === null) return { label: 'new', good: true };
  if (row.rank === null) return { label: 'out', good: false };
  return row.rank < row.baselineRank
    ? { label: `▲${row.baselineRank - row.rank}`, good: true }
    : { label: `▼${row.rank - row.baselineRank}`, good: false };
}

/** Factor slices as % of the score (points ÷ Σpoints), top 3. */
function slices(breakdown: BreakdownItem[] | null) {
  const items = (breakdown ?? []).filter((b) => (b.points ?? 0) > 0);
  const total = items.reduce((sum, b) => sum + (b.points ?? 0), 0);
  return items
    .sort((a, b) => (b.points ?? 0) - (a.points ?? 0))
    .slice(0, 3)
    .map((b) => ({ label: FACTOR_LABEL[b.factor] ?? b.factor, share: Math.round(((b.points ?? 0) / total) * 100) }));
}

export function ProviderCard({ row, era, bestMatchId, flags }: { row: Row; era: ScoreEra | null; bestMatchId?: string; flags?: { userSelected: boolean; confirmed: boolean } }) {
  const change = delta(row);
  const excluded = row.rank === null;
  // Bar only on the pts scale (score 0–1); legacy raw sums have no fixed maximum.
  const bar = era === 'C' && row.score !== null ? Math.min(100, Math.max(0, Math.round(row.score * 100))) : null;
  return (
    <li className={`flex gap-3.5 border-t border-[#efeae1] px-5 py-4 first:border-t-0 ${excluded ? 'bg-[#fafafa] opacity-[.55]' : ''}`}>
      <div className="flex w-11 flex-none flex-col items-center gap-0.5">
        <span className="text-xl font-bold text-[#1b2232]">{row.rank ? `#${row.rank}` : '–'}</span>
        {change && (
          <span className={`text-xs font-bold ${change.good ? 'text-bquest-green' : 'text-[#9a3412]'}`}>{change.label}</span>
        )}
      </div>
      <div className="flex min-w-0 flex-1 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[15px] font-bold text-[#1b2232]">{row.name}</span>
          {row.rank === 1 && <span className="rounded-full bg-[#e3efec] px-2 py-0.5 text-xs font-bold text-[#014d41]">Top Match</span>}
          {row.rank !== null && row.providerId === bestMatchId && (
            <span title="Highest score in the whole plan" className="rounded-full bg-[#b7791f] px-2 py-0.5 text-xs font-bold text-white">
              Best Match
            </span>
          )}
          {row.cutReason && (
            <span className="rounded-full bg-[#eceef3] px-2 py-0.5 text-xs font-bold text-[#3d4659]">{CUT_LABEL[row.cutReason]}</span>
          )}
          {flags?.userSelected && <span className="rounded-full bg-[#eceef3] px-2 py-0.5 text-xs font-bold text-[#3d4659]">Selected by user</span>}
          {flags?.confirmed && <span className="rounded-full bg-[#eceef3] px-2 py-0.5 text-xs font-bold text-[#3d4659]">Confirmed</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2.5 text-sm">
          {bar !== null && (
            <div aria-hidden="true" className="h-2 max-w-[280px] flex-1 basis-24 overflow-hidden rounded bg-[#efeae1]">
              <div className={`h-full ${excluded ? 'bg-[#b8bfcc]' : 'bg-bquest-green'}`} style={{ width: `${bar}%` }} />
            </div>
          )}
          <span className="font-bold tabular-nums text-[#1b2232]">{scoreLabel(row.score, era)}</span>
          {(row.relevance !== null || row.quality !== null) && (
            <span className="tabular-nums text-[#56607a]">
              {row.relevance !== null && <span title="Does not change with weights">fit {pts(row.relevance)}</span>}
              {row.relevance !== null && row.quality !== null && ' · '}
              {row.quality !== null && `quality ${pts(row.quality)}`} (of 100)
            </span>
          )}
        </div>
        {era !== 'A' && (
          <div className="flex flex-wrap gap-1.5 text-xs text-[#3d4659]">
            {slices(row.breakdown).map((s) => (
              <span key={s.label} className="rounded-md bg-[#f4f0e9] px-2 py-0.5">
                {s.label} <strong className="font-bold">{s.share}%</strong>
              </span>
            ))}
          </div>
        )}
      </div>
    </li>
  );
}

/**
 * Every factor's contribution to the Best Match score, in pts (points × 100), within the
 * config of the shown view. Deliberately no cross-config point deltas.
 */
export function BestMatchBreakdown({ breakdown }: { breakdown: BreakdownItem[] | null }) {
  const items = (breakdown ?? []).filter((b) => b.points !== undefined);
  if (!items.length) return null;
  return (
    <div role="group" aria-label="Best Match breakdown" className="flex w-full flex-wrap gap-1.5 text-xs text-[#3d4659]">
      {items.map((b) => (
        <span key={b.factor} className="rounded-md bg-[#f4f0e9] px-2 py-0.5">
          <span>{FACTOR_LABEL[b.factor] ?? b.factor}</span> <strong>{pts(b.points ?? 0)} pts</strong>
        </span>
      ))}
    </div>
  );
}
